import { useState } from "react";

import { useDispatch } from "react-redux";

import { requestTestRuleBook } from "actions";

import { PriceComponentsName } from "constants/billingLedgerEntries";

import toast from "lib/toast";

interface RawRuleTesterLedgerEntry {
  billing_tags: number[];
  from_business: string;
  gl_code: string;
  id: string;
  invoice_line_item: string;
  item_id: string;
  item_type: string;
  price_cents: string;
  price_components: { name: string; value: string }[];
  quantity: string;
  reference_object: string;
  rule_id: string;
  rule_name: string;
  tax_type: string;
  title: string;
  to_business: string;
}

// The rule book as sent to MinceNous, see `get_ruleset_content_json`.
interface RuleTesterRule
  extends Pick<
    RuleEditableFields,
    | "apply_criteria_filter_to_splits"
    | "apply_splits"
    | "comment"
    | "criteria"
    | "gl_code"
    | "id"
    | "input_field"
    | "invoice_line_item_template"
    | "master_rule_id"
    | "name"
    | "order"
    | "prerequisite_rule_ids"
    | "quantity_output_format"
    | "quantity_raw_format"
    | "separate_document_key"
    | "title_template"
    | "unit_amount_output_format"
    | "unit_amount_raw_format"
  > {
  rule_set: string | null;
}

interface RuleTesterRuleSet extends Pick<RuleSet, "criteria" | "name"> {
  cbid: string;
  input_field: string | null;
  rules: RuleTesterRule[];
}

// Validated by the backend's `RuleBookSerializer`, which only reads `rule_sets`.
interface RuleTesterRuleBookData {
  rule_sets: (RuleSet & { rules: RuleEditableFields[] })[];
}

interface RawRuleTesterResults {
  ledger_entries: RawRuleTesterLedgerEntry[];
  rulebook: RuleTesterRuleSet[];
  sale_data: unknown;
}

export interface RuleTesterLedgerEntry {
  billingTags: number[];
  fromBusinessId: string;
  glCode: string;
  id: string;
  invoiceLineItem: string;
  itemId: string;
  itemType: string;
  quantity: string;
  referenceObject: string;
  ruleId: string;
  ruleName: string;
  subtotal?: string;
  tagIds: number[];
  taxAmount?: string;
  taxType: string;
  title: string;
  toBusinessId: string;
  unitAmount: string;
}

export interface RuleTesterLedgerEntryAggridRow {
  ledgerEntry: RuleTesterLedgerEntry;
  fromBusiness?: Business;
  toBusiness?: Business;
}

interface RuleTesterResults {
  ledgerEntries: RuleTesterLedgerEntry[];
  // TODO: Type once lib/saleData and the RuleTester results are typed.
  saleData: unknown;
  rulebook: RuleTesterRuleSet[];
}

const deserializeRawLedgerEntry = (
  rawLedgerEntry: RawRuleTesterLedgerEntry,
): RuleTesterLedgerEntry => ({
  billingTags: rawLedgerEntry.billing_tags,
  fromBusinessId: rawLedgerEntry.from_business,
  glCode: rawLedgerEntry.gl_code,
  id: rawLedgerEntry.id,
  invoiceLineItem: rawLedgerEntry.invoice_line_item,
  quantity: rawLedgerEntry.quantity,
  referenceObject: rawLedgerEntry.reference_object,
  ruleName: rawLedgerEntry.rule_name,
  subtotal: rawLedgerEntry.price_components.find(
    pc => pc.name === PriceComponentsName.Subtotal,
  )?.value,
  tagIds: rawLedgerEntry.billing_tags, // ref lib.serializers.ledgerEntries.js serializeMap
  taxAmount: rawLedgerEntry.price_components.find(
    pc => pc.name === PriceComponentsName.GST,
  )?.value,
  taxType: rawLedgerEntry.tax_type,
  title: rawLedgerEntry.title,
  toBusinessId: rawLedgerEntry.to_business,
  unitAmount: rawLedgerEntry.price_cents,
  ruleId: rawLedgerEntry.rule_id,
  itemId: rawLedgerEntry.item_id,
  itemType: rawLedgerEntry.item_type,
});

const deserializeResults = (
  result: RawRuleTesterResults,
): RuleTesterResults => ({
  ledgerEntries: result.ledger_entries.map(deserializeRawLedgerEntry),
  saleData: result.sale_data,
  rulebook: result.rulebook,
});

export function useRuleTester() {
  const [results, setResults] = useState<RuleTesterResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  function onTestRules(
    livestockSaleIds: number[],
    ruleBookId: string | undefined,
    ruleBookData: RuleTesterRuleBookData | undefined,
    onSuccess: (results: RawRuleTesterResults) => void,
    onError: () => void,
  ) {
    dispatch(
      requestTestRuleBook(livestockSaleIds, ruleBookId, ruleBookData, {
        onSuccess,
        onError,
      }),
    );
  }

  function testRules(
    livestockSaleIds: number[],
    ruleBookId: string | undefined,
    ruleBookData: RuleTesterRuleBookData | undefined,
  ) {
    onTestRules(
      livestockSaleIds,
      ruleBookId,
      ruleBookData,

      rawResults => {
        setResults(deserializeResults(rawResults));
        setIsLoading(false);
      },
      () => {
        toast.error("An error occurred testing the Rule Books.");
        setIsLoading(false);
      },
    );
    setIsLoading(true);
  }

  return [results, isLoading, testRules] as const;
}
